import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Icon, useInternal } from "uikit";

import { useAppContext } from "../../../../context";
import { CardModal, StyledP, StyledRow, StyledWrapIcon } from "../../../common";

import TimeSelect, { TimeSelectMemo } from "./TimeSelect";

const Wrap = styled(StyledRow)`
	justify-content: center;
	align-items: center;
	height: 28px;
	padding-inline: 10px;
`;

const OrderTimeSelect: React.FC<OrderTimeSelect.Props> = ({
	value,
	onChange,
	width = "auto",
	displayModal = false,
	onChangeDisplayModal,
	disabled = false,
}) => {
	const { t } = useTranslation();
	const { dateFns } = useAppContext();

	const [display, setDisplay] = useInternal<boolean>(displayModal);
	const [isChange, setIsChange] = useInternal<boolean>(false);
	const [nowLater, setNowLater] = useInternal<boolean>(false);
	const [error, setError] = useState<boolean>(false);

	const [BaseValue, setBaseValue] = useState<TimeSelect.ScheduledTime>({
		on: "later",
		time: dateFns.toZonedTime(new Date()),
	});

	const [inputValue, setInputValue] = useState<TimeSelect.ScheduledTime>({
		on: "later",
		time: value || dateFns.toZonedTime(new Date()),
	});

	useLayoutEffect(() => {
		if (!isChange && value) {
			setIsChange(true);
			setBaseValue({
				on: value ? "later" : "now",
				time: value,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isChange, value]);

	useLayoutEffect(() => {
		setNowLater(Boolean(value));
		setInputValue((prev) => ({
			...prev,
			time: value || dateFns.toZonedTime(new Date()),
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const onClick = useCallback(() => {
		if (!disabled) {
			setDisplay(true);
			onChangeDisplayModal?.(true);
		}
	}, [disabled, setDisplay, onChangeDisplayModal]);

	const onClose = useCallback(() => {
		setDisplay(false);
		onChangeDisplayModal?.(false);
		setInputValue(BaseValue);
		if (onChange) {
			setInputValue(() => ({
				time: value || BaseValue.time,
				on: "later",
			}));
			onChange(value);
		}
	}, [setDisplay, onChangeDisplayModal, BaseValue, onChange, value]);

	useEffect(() => {
		if (disabled) setDisplay(false);
	}, [disabled, setDisplay]);

	const onSubmit = useCallback(() => {
		const entryTime = new Date(inputValue.time).getTime();
		const nowTime = Date.now() - 30_000;

		if (nowTime > entryTime) {
			setError(true);
			return;
		}
		setError(false);

		if (onChange) {
			if (inputValue.on === "now") {
				setIsChange(false);
				onChange(null);
				setInputValue((prev) => ({ ...prev, on: "later" }));
			}
			if (inputValue.on === "later") {
				if (nowTime > entryTime) {
					const time = dateFns.toZonedTime(new Date());
					time.setSeconds(0, 0);
					setIsChange(false);
					onChange(null);
					setInputValue({ time, on: "later" });
				} else {
					setIsChange(false);
					onChange(inputValue.time);
				}
			}
		}
		setDisplay(false);
		onChangeDisplayModal?.(false);
	}, [
		inputValue.time,
		inputValue.on,
		onChange,
		setDisplay,
		onChangeDisplayModal,
		setIsChange,
		dateFns,
	]);

	const styles = useMemo(
		() => ({
			arrow: {
				w: "16px",
				h: "16px",
				colors: disabled ? "#B5C0C7" : "#5e6b73",
				cursor: disabled ? "not-allowed" : "pointer",
			},
			time: {
				cursor: disabled ? "not-allowed" : "pointer",
				colors: disabled ? "#9BA3A8" : "#21333F",
				over: "hidden",
			},
		}),
		[disabled],
	);

	const time = useMemo(
		() =>
			(inputValue.on === "later" && !nowLater) || inputValue.on === "now"
				? t([`orderPage.order_form.order_time.now`, "Not now"])
				: dateFns.formatTime2(inputValue.time, "HH:mm dd/MM/yyyy"),
		[dateFns, inputValue.on, inputValue.time, nowLater, t],
	);

	return (
		<Wrap w={width} bgC={`#F6F6F6`} h="378px" onClick={onClick}>
			{display && (
				<CardModal
					title={
						t(`orderPage.order_form.order_time.time_select`) ?? ""
					}
					onClose={onClose}
					onSave={onSubmit}
				>
					<TimeSelectMemo
						value={inputValue}
						setValue={setInputValue}
						onSubmit={onSubmit}
						onClose={onClose}
						error={error}
						setError={setError}
					/>
				</CardModal>
			)}

			<StyledP {...styles.time} text={{ tt: "none", ws: "nowrap" }}>
				{time}
			</StyledP>
			<StyledWrapIcon {...styles.arrow}>
				<Icon id="arrow-select-down" size={16} />
			</StyledWrapIcon>
		</Wrap>
	);
};

declare namespace OrderTimeSelect {
	interface Props {
		width?: string;
		value: Date | null;
		onChange?: (value: Date | null) => void;
		onChangeDisplayModal?: (displayModal?: boolean) => void;
		displayModal?: boolean;
		disabled?: boolean;
	}
}

export const OrderTimeSelectMemo = memo(OrderTimeSelect);
export default OrderTimeSelect;
